import { Component, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { NgxSpinnerService } from 'ngx-spinner';
import { SharedService } from '../../../../services/shared/shared.service';
import { FirestoreService } from '../../../../services/firestore/firestore.service';
import { NotificationService } from '../../../../services/notification/notification.service';
import { Article } from '../../../../models/article';
import { NotificationRequest } from '../../../../models/notification-request';
import { Timestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-articles',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterLink, CKEditorModule],
  templateUrl: './articles.component.html',
  styleUrls: ['./articles.component.css'],
})
export class ArticlesComponent implements OnInit {
  articles: Article[] = [];
  currentArticle: Article = {
    title: '',
    content: '',
    status: 'draft',
    id: '',
  };
  editMode = false;
  selectedFile?: File;
  showSpinner = true;
  customCategory = '';
  categories: string[] = [];
  selectedCategory: string = '';
  tagsInput: string = '';

  // CKEditor configuration
  public Editor: any = null; // Initialize as null
  public editorConfig = {
    toolbar: [
  'heading',
  '|',
  'bold',
  'italic',
  'link',
  'bulletedList',
  'numberedList',
  '|',
  'blockQuote',
  'insertTable',
  'undo',
  'redo',
],
    placeholder: 'Enter article content here...',
  };

  constructor(
    public sharedService: SharedService,
    private spinner: NgxSpinnerService,
    private firestoreService: FirestoreService,
    private notificationService: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  async ngOnInit(): Promise<void> {
    // Load CKEditor only in the browser
    if (isPlatformBrowser(this.platformId)) {
      const ClassicEditor = await import('@ckeditor/ckeditor5-build-classic');
      this.Editor = ClassicEditor.default || ClassicEditor;
    }
    this.getAllArticles();
    this.loadCategories();
  }


  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

  getAllArticles() {
    this.firestoreService.getAll('articles').subscribe({
      next: (data) => {
        this.articles = data;
        this.showSpinner = false;
      },
      error: (error) => {
        this.showSpinner = false;
        this.sharedService.handleError(error, undefined, "Error fetching articles.");
      }
    });
  }

saveArticle(form: NgForm) {
  this.spinner.show();
  if (form.invalid) {
    this.spinner.hide();
    return;
  }

  if (this.customCategory) {
    this.currentArticle.category = this.customCategory;
  }

  if (this.currentArticle.status === 'published' && !this.currentArticle.publishedAt) {
    this.currentArticle.publishedAt = Timestamp.now();
  } 

  const saveOrUpdate = () => {
    if (this.editMode && this.currentArticle.id) {
      // ✅ Update existing article
      this.firestoreService.update('articles', this.currentArticle.id, this.currentArticle).subscribe({
        next: () => {
          this.spinner.hide();
          this.resetForm();
          this.getAllArticles();
          this.sharedService.handleSuccess("Article updated successfully");
        },
        error: (err) => {
          console.error('Error updating article:', err);
          this.spinner.hide();
          this.sharedService.handleError(err, undefined, "Error updating article.");
        }
      });
    } else {
      // ✅ Add new article
      this.firestoreService.add('articles', this.currentArticle).subscribe({
        next: () => {
          this.sharedService.handleSuccess("Article added successfully");
         
          this.getAllArticles();

          // 🚀 Send notification to all users
          this.firestoreService.getUserIds().subscribe({
            next: userIds => {
              console.log("test get users ids")
              console.log(userIds)
              if (userIds && userIds.length > 0) {
                const request =new NotificationRequest()
                  request.title= "New Article Published",
                  request.body= `Check out our new article= "${this.currentArticle.title}"`,
                  request.type= "NEW_ARTICLE",
                  request.userIds= userIds,
                  request.status= "UNREAD",
                this.notificationService.saveNotification(request).subscribe({
                  next: () => this.resetForm(),
                });
              }
            },
            error: err => this.sharedService.handleError(err, undefined, "Error fetching user IDs.")
          });
        },
        error: (err) => {
          console.error('Error adding article:', err);
          this.spinner.hide();
          this.sharedService.handleError(err, undefined, "Error adding article.");
        }
      });
    }
  };

  // ✅ Handle file upload if needed
  if (this.selectedFile) {
    const filePath = `article-images/${Date.now()}_${this.selectedFile.name}`;
    this.firestoreService.uploadFile(filePath, this.selectedFile).subscribe({
      next: (url) => {
        this.currentArticle.coverUrl = url;
        saveOrUpdate();
      },
      error: (error) => {
        console.error('File upload error:', error);
        this.spinner.hide();
        this.sharedService.handleError(error, undefined, "Error uploading cover image.");
      }
    });
  } else {
    saveOrUpdate();
  }
}


  editArticle(article: Article) {
    this.currentArticle = {
      ...article,
      views: article.views || 0, // Preserve views or default to 0
      likesCount: article.likesCount || 0, // Preserve likesCount or default to 0
      coverUrl: article.coverUrl || undefined, // Preserve coverUrl
      category: article.category || undefined, // Preserve category
      authorName: article.authorName || undefined, // Preserve authorName
      publishedAt: article.publishedAt 
    };
    this.customCategory = article.category === 'Other' ? article.category : '';
    this.editMode = true;
    this.selectedFile = undefined;
    const modal = new (window as any).bootstrap.Modal(document.getElementById('articleModal'));
    modal.show();
  }

  deleteArticle(id: string) {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This action will delete the article permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.firestoreService.delete('articles', id).subscribe({
          next: () => {
            this.spinner.hide();
            this.getAllArticles();
            Swal.fire('Deleted!', 'The article has been deleted.', 'success');
          },
          error: (err) => {
            this.spinner.hide();
            this.sharedService.handleError(err, undefined, "There was an error deleting the article.");
          }
        });
      }
    });
  }

  resetForm() {
    this.currentArticle = { title: '', content: '', status: 'draft', id:"" };
    this.editMode = false;
    this.selectedFile = undefined;
    this.tagsInput = '';
    this.customCategory = '';
    this.closeModal();
  }

  private closeModal() {
    const modalEl = document.getElementById('articleModal');
    if (modalEl) {
      const modalInstance = (window as any).bootstrap.Modal.getInstance(modalEl);
      modalInstance?.hide();
    }
  }

  loadCategories() {
    this.firestoreService.getAll('articles').subscribe({
      next: (articles: Article[]) => {
        const categories = Array.from(new Set(articles.map(article => article.category).filter(cat => cat)));
        this.categories = categories as string[];
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.sharedService.handleError(error, undefined, "Error loading categories.");
      }
    });
  }

  filterByCategory() {
    this.showSpinner = true;
    if (this.selectedCategory) {
      this.firestoreService.getByField('articles', 'category', this.selectedCategory).subscribe({
        next: (articles) => {
          this.articles = articles;
          this.showSpinner = false;
        },
        error: (error) => {
          this.showSpinner = false;
          this.sharedService.handleError(error, undefined, "Error filtering articles.");
        }
      });
    } else {
      this.getAllArticles();
    }
  }
}